import React, { useState } from 'react';
import { clsx } from 'clsx';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import { useOmsReturnStatus, useRestartOmsWorkflow } from '@/hooks/item';
import { ServiceErrorView } from '@/components/atoms/Error';
import { Item } from '@/types/item';
import { ReturnStatus, ReturnStatusData, ReturnStatusKind, LitigationType } from '@/types/omsStatus';
import { transformDateStringFrance } from '@/utilities/datetime';
import { GridAction } from '@/components/molecules/section/Grid';
import Container from '@/components/molecules/section/Container';
import styles from './OmsReturnStatus.module.scss';
import ModalView from '@/components/modals/Modal';
import { Modal } from '@/types/modal';
import { Dropdown } from '@/components/molecules/common/Form';
import { environments } from '@/environment';
import { BootService } from '@/services/boot';
import { useErrorHandling } from '@/hooks/common';

const ShipmentType = 'Shipment';
const ReturnType = 'Returns';
const FilterOutStatus = ['BuyerInstantRefund'];

const returnToSellerFlag = '36';

const ignoredStatuses = [
    'FailSellerPayoutExecution',
    'SellerReturnConformityRefundBuyer',
    'FailedSellerReturnConformityNoDecision',
    'SellerReturnConformityPaySeller',
    'SellerReturnConformityReported',
    'FailedSellerPayoutClosed'
];
const ignoreCustomStatusCheck = (status?: string) => status && ignoredStatuses.includes(status);

const View: React.FC<{ item: Item }> = ({ item }) => {
  const { t } = useTranslation();

  const { getErrorMsgFromResponse } = useErrorHandling();

  const data = useOmsReturnStatus(item.productId);

  const { mutate: restartReturnWorkflow, isLoading: restarting } = useRestartOmsWorkflow();

  const flags = item.flags ? item.flags.map(({ id }) => id) : [];

  const [modal, setModal] = useState<Modal | null>(null);
  const [returnStatusIndex, setReturnStatusIndex] = useState('0');

  const handleRestartWorkflow = () => {
    restartReturnWorkflow(
      { productId: item.productId },
      {
        onSuccess: () =>
          setModal({ kind: 'RestartOmsWorkflow', onClose: () => setModal(null) }),
        onError: (error) => {
          setModal({
            kind: 'Error',
            title: t('item.restart_oms_workflow.title'),
            message: getErrorMsgFromResponse(error),
            onClose: () => setModal(null),
          });
        },
      },
    );
  };

  const actions: GridAction[] = [
    {
      kind: 'Button',
      label: t('oms_return_status.actions.create_return_litigation'),
      onClick: () => {
        setModal({
          kind: 'ItemCreateReturnLitigation',
          litigationType: LitigationType.RETURN,
          item,
          onClose: () => setModal(null),
        });
      },
    },
    {
      kind: 'Button',
      label: t('oms_return_status.actions.replace_ticket'),
      onClick: () => {
        setModal({ kind: 'ItemReplaceReturnLitigation', item, onClose: () => setModal(null) });
      },
      disabled: data.kind === 'Error',
    },
    {
      kind: 'Button',
      label: t('oms_return_status.actions.create_seller_return_litigation'),
      onClick: () => {
        setModal({ kind: 'ItemCreateSellerReturnLitigation', item, onClose: () => setModal(null) });
      },
    },
    {
      kind: 'Button',
      label: t('oms_return_status.actions.restart_workflow'),
      onClick: () => handleRestartWorkflow(),
      disabled: restarting,
    },
  ];

  if (data.kind === 'Called' && data.fetching) {
    return (
      <Skeleton
        height={100}
        variant="rounded"
      />
    );
  }

  if (data.kind === 'Error') {
    return (
      <>
        <Container
          title={t('oms_return_status.title')}
          actions={actions}
          testId="oms_return_status"
        ></Container>
        <ServiceErrorView error={data.error} />
        <ModalView modal={modal} />
      </>
    );
  }

  if (data.kind === 'Loaded') {
    const returnStatuses = data.data.data.map((returnStatus, index) => ({
      label: returnStatus.returnId.toString(),
      value: index.toString(),
      display: (
        <div className={styles.container}>
          {'Zendesk #' + returnStatus.zendeskId + ' - Return #' + returnStatus.returnId}
          <div className={styles.statusInfo}>
            <div className={styles.status}>{t('item.return_status.current_status')}</div>
            {headerStatus(t, returnStatus)}
          </div>
        </div>
      ),
    }));

    const returnStatus = data.data.data[Number(returnStatusIndex)];

    return (
      <>
        <Container
          title={t('oms_return_status.title')}
          actions={actions}
        >
          <div className={styles.container}>
            <div className={styles.containerGrey}>
              <div className={styles.threeInfo}>
                <div>{`${t('item.return_status.item')} #${item.productId}`}</div>
              </div>
              <Dropdown
                required={true}
                disabled={false}
                label={t('oms_return_status.dropdown_title')}
                value={returnStatusIndex}
                onSelect={setReturnStatusIndex}
                items={returnStatuses}
              />
              {moreInfo(t, returnStatus, item, flags)}
            </div>

            {!!returnStatus && (
              <a
                className={styles.threeInfo}
                style={{ padding: '1rem 0 0 2.5rem', textDecoration: 'underline', color: '#1976D2' }}
                href={`${environments[BootService.env].zendeskUrl}/agent/tickets/${returnStatus.zendeskId}`}
                target="_blank"
                rel="noreferrer"
              >{`Zendesk #${returnStatus.zendeskId}`}</a>
            )}

            <div className={styles.statusList}>
              {returnStatus.data.map((item, i) => {
                if (
                  item.status != undefined &&
                  item.status.kind != undefined &&
                  !FilterOutStatus.includes(item.status.kind)
                ) {
                  return statusView(t, item, i === 0, i === returnStatus.data.length - 1, i.toString());
                }
              })}
            </div>
          </div>
        </Container>
        <ModalView modal={modal} />
      </>
    );
  }
};

function headerStatus(t: TFunction, { status, data }: ReturnStatus): string {
  if (status?.kind === 'AllowReturn') {
    const first = customStatus(data, { kind: 'AgentAllowReturn' });
    if (first) {
      return t(`oms_return_status.Custom.agent_allow_return_status`);
    }
  }

  if (data.length > 0) {
    return titleStatus(t, status, data[0].operator);
  }
  return titleStatus(t, status);
}

function titleStatus(t: TFunction, status?: ReturnStatusKind | null, operator?: string | null): string {
  if (status == null) {
    return t('item.return_status.missing_status');
  } else if (status.kind === 'Invalid') {
    return status.value;
  } else {
    let typeText = t(`oms_return_status.${status.kind}.type`);
    let title = t(`oms_return_status.${status.kind}.title`);

    if (status.kind === 'Both' && !!operator) {
      if (operator.includes(ShipmentType)) {
        typeText = t(`oms_return_status.${status.kind}.operator_shipment_type`);
        title = t(`oms_return_status.${status.kind}.operator_shipment_title`);
      } else if (operator.includes(ReturnType)) {
        typeText = t(`oms_return_status.${status.kind}.operator_return_type`);
        title = t(`oms_return_status.${status.kind}.operator_return_title`);
      }
    }

    return typeText === '' ? title : `${typeText} ${title}`;
  }
}

function moreInfo(
  t: TFunction,
  { status, data }: ReturnStatus,
  sectionData: Item,
  flags: string[],
): React.ReactElement {
  const location = () => {
    const first = customStatus(data, { kind: 'AgentAllowInstantRefund' });
    const second = customStatus(data, { kind: 'Delivered' });
    const third = customStatus(data, { kind: 'Disagree' });

    if (sectionData.inStock === true) {
      return t(`oms_return_status.Custom.item_in_warehouse`);
    } else if (sectionData.isLost && sectionData.isLost === '1') {
      return t(`oms_return_status.Custom.item_is_lost`);
    } else if (first != null) {
      return t(`oms_return_status.Custom.item_is_with_buyer`);
    } else if (second != null) {
      if (flags.includes(returnToSellerFlag)) {
        return t(`oms_return_status.Custom.item_has_been_returned_to_seller`);
      }
      return t(`oms_return_status.Custom.item_has_been_returned_to_warehouse`);
    } else if (third != null) {
      return t(`oms_return_status.Custom.item_is_with_buyer`);
    }
    return status ? t(`oms_return_status.${status.kind}.item_location`) : t(`item.return_status.missing_status`);
  };

  const seller = () => {
    const defaultSellerPayout = status
      ? t(`oms_return_status.${status.kind}.seller_payout`)
      : t(`item.return_status.missing_status`);

    const isPassCustomStatusCheck = ignoreCustomStatusCheck(status?.kind);

    if (isPassCustomStatusCheck) return defaultSellerPayout;

    const payoutDoneAsOnHold = customStatus(data, {
      kind: 'PayoutDoneAsOnHold',
    });
    const payoutDoneAsRelease = customStatus(data, {
      kind: 'PayoutDoneAsRelease',
    });
    const payoutDoneAsCancel = customStatus(data, {
      kind: 'PayoutDoneAsCancel',
    });

    const first = payoutDoneAsCancel || payoutDoneAsRelease || payoutDoneAsOnHold;
    const second = customStatus(data, { kind: 'SellerPayoutSuccess' });
    const third =
      customStatus(data, { kind: 'Disagree' }) ||
      customStatus(data, { kind: 'Both' }) ||
      customStatus(data, { kind: 'SellerPayoutRelease' }) ||
      customStatus(data, { kind: 'PayoutForceRelease' });

    const fourth = customStatus(data, { kind: 'SellerPayoutCancel' }) || customStatus(data, { kind: 'PayoutCancel' });

    if (first != null) {
      return t(`oms_return_status.Custom.seller_has_been_paid`);
    } else if (second != null) {
      return t(`oms_return_status.Custom.seller_has_been_paid`);
    } else if (failStatus(data)) {
      return failStatus(data);
    } else if (third != null) {
      return t(`oms_return_status.Custom.seller_should_be_paid`);
    } else if (sectionData.isLost && sectionData.isLost === '1') {
      return t(`oms_return_status.Custom.seller_payout_should_be_released`);
    } else if (fourth != null) {
      return t(`oms_return_status.Custom.seller_will_not_get_paid`);
    }
    return defaultSellerPayout;
  };

  const buyer = () => {
    const defaultSellerPayout = status
      ? t(`oms_return_status.${status.kind}.buyer_refund`)
      : t(`item.return_status.missing_status`);

    const isPassCustomStatusCheck = ignoreCustomStatusCheck(status?.kind);
    if (isPassCustomStatusCheck) return defaultSellerPayout;

    const first = customStatus(data, { kind: 'BuyerRefundSuccess' });
    const second = customStatus(data, { kind: 'Agree' }) || customStatus(data, { kind: 'Both' });
    const third = customStatus(data, { kind: 'Disagree' });

    if (first != null) {
      return t(`oms_return_status.Custom.buyer_refund_success`);
    } else if (failStatus(data)) {
      return failStatus(data);
    } else if (
      (sectionData.isLost && sectionData.isLost === '1') ||
      (customStatus(data, { kind: 'DeliveredToSeller' }) &&
        !customStatus(data, { kind: 'FailedSellerReturnConfirmity' }) &&
        !customStatus(data, { kind: 'SellerReturnConformityReported' }) &&
        !customStatus(data, { kind: 'FailedReturnPostalLitigation' }))
    ) {
      return t(`oms_return_status.Custom.buyer_should_be_refunded`);
    } else if (second != null) {
      return t(`oms_return_status.Custom.buyer_to_be_refunded`);
    } else if (third != null) {
      return t(`oms_return_status.Custom.buyer_will_not_be_refunded`);
    }

    return defaultSellerPayout;
  };

  function failStatus(data: ReturnStatusData[]): string | null {
    for (let i = 0; i < data.length; i++) {
      const e = data[i];
      if (e.status == null || e.status === undefined) {
        continue;
      }
      if (e.status.kind === 'Invalid') {
        if (e.status.value.startsWith('FAIL_')) {
          return t('oms_return_status.Custom.fail');
        }
      }
    }

    return null;
  }

  return (
    <div className={styles.moreContainer}>
      <div className={styles.moreItem}>
        <div className={styles.moreLabel}>{t('item.return_status.item_location')}</div>
        <div className={styles.moreText}>{location()}</div>
      </div>
      <div className={styles.moreItem}>
        <div className={styles.moreLabel}>{t('item.return_status.seller_payout')}</div>
        <div className={styles.moreText}>{seller()}</div>
      </div>
      <div className={styles.moreItem}>
        <div className={styles.moreLabel}>{t('item.return_status.buyer_refund')}</div>
        <div className={styles.moreText}>{buyer()}</div>
      </div>
    </div>
  );
}

function statusView(
  t: TFunction,
  { status, operator, updatedAt }: ReturnStatusData,
  isFirst: boolean,
  isLast: boolean,
  key: string,
): React.ReactElement {
  return (
    <div
      key={key}
      className={clsx(
        styles.statusContainer,
        isFirst ? styles.statusContainerFirst : '',
        isLast ? styles.statusContainerLast : '',
      )}
    >
      <div className={styles.statusContent}>
        <div className={styles.statusTitle}>{titleStatus(t, status, operator)}</div>
        <div className={styles.statusText}>{operator}</div>
        <div className={styles.statusText}>{transformDateStringFrance(updatedAt)}</div>
      </div>
    </div>
  );
}

/**
 * Finds the most recent occurrence of a specific status kind in the status history
 * @param data Array of ReturnStatusData containing status history
 * @param item The ReturnStatusKind to search for
 * @returns The position of the most recent occurrence (1-based index from end) or null if not found
 *
 * Example:
 * If data has 5 items and the matching status is at index 2:
 * Returns 3 (5 - 2) since it's the 3rd most recent occurrence
 */
function customStatus(data: ReturnStatusData[], item: ReturnStatusKind): number | null {
  const count = data.length;
  return data.reduce((accumulator: number | null, { status }, index) => {
    return status && status.kind === item.kind ? count - index : accumulator;
  }, null);
}

export default View;
